import type { Theme } from '@mui/material';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Iconify } from '../../components/iconify';
import { StatusChip } from '../../components/status-chip';
import { Box, Avatar, Skeleton, Stack, Switch, useTheme } from '@mui/material';

import { imageOutlineSx } from '../../theme/surfaces';
import { getConnectorIcon, getConnectorBgColor, getConnectorAssetUrl } from '../types';

import type { ConnectorRecord, ConnectorState } from '../types';

// microsoft_outlook_calendar ships its own baked-in rounded background (see
// libs/ari-lib/src/assets/icons/outlook-calendar.svg), so it renders at full tile
// size instead of inset like the other asset icons.
const FULL_TILE_ASSET_CONNECTOR_IDS = new Set(['microsoft_outlook_calendar']);

/** The 24px tile every connector row leads with. */
export function ConnectorTile({ bgcolor, children }: { bgcolor?: string; children: React.ReactNode }) {
  return (
    <Avatar
      variant="rounded"
      sx={(theme) => ({
        width: 24,
        height: 24,
        borderRadius: 0.5,
        bgcolor: bgcolor ?? theme.palette.container.high,
        flexShrink: 0,
        ...imageOutlineSx(theme),
      })}
    >
      {children}
    </Avatar>
  );
}

export function ConnectorIcon({
  connector,
}: {
  connector: Pick<ConnectorRecord, 'id' | 'iconify_logo'> & Partial<Pick<ConnectorRecord, 'type'>>;
}) {
  const theme = useTheme();
  const assetUrl = getConnectorAssetUrl(connector.id);
  const assetSize = FULL_TILE_ASSET_CONNECTOR_IDS.has(connector.id) ? 24 : 18;
  return (
    <ConnectorTile bgcolor={getConnectorBgColor(connector.id, theme.palette.container.high)}>
      {assetUrl ? (
        <Box component="img" src={assetUrl} alt="" sx={{ width: assetSize, height: assetSize, objectFit: 'contain' }} />
      ) : (
        <Iconify icon={getConnectorIcon(connector)} width={16} />
      )}
    </ConnectorTile>
  );
}

export function ConnectorRowSkeleton() {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        height: 42,
        px: 1,
        borderRadius: 1,
        border: `1px solid ${theme.palette.grey[700]}`,
      })}
    >
      <Skeleton variant="rounded" width={24} height={24} sx={{ borderRadius: 0.5, flexShrink: 0 }} />
      <Skeleton variant="text" width={120} height={20} sx={{ flex: 0 }} />
      <Box sx={{ flex: 1 }} />
      <Skeleton variant="rounded" width={70} height={24} sx={{ borderRadius: 1 }} />
    </Box>
  );
}

export function ConnectorsSkeleton() {
  return (
    <Stack spacing={1.5}>
      <Box>
        <Skeleton variant="text" width={70} height={16} sx={{ mb: 1 }} />
        <Stack spacing={1}>
          {Array.from({ length: 2 }).map((_, i) => (
            <ConnectorRowSkeleton key={`c-${i}`} />
          ))}
        </Stack>
      </Box>
      <Box>
        <Skeleton variant="text" width={60} height={16} sx={{ mb: 1 }} />
        <Stack spacing={1}>
          {Array.from({ length: 4 }).map((_, i) => (
            <ConnectorRowSkeleton key={`f-${i}`} />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

export const connectorRowSx = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  height: { xs: 48, md: 42 },
  px: 1,
  borderRadius: 1,
  border: `1px solid ${theme.palette.grey[700]}`,
  overflow: 'hidden',
});

/** The switch flips the user's own row. A row the user never linked has none. */
export function hasUserLink(state: ConnectorState): boolean {
  return state !== 'disconnected';
}

/** Off keeps the row and its auth. Only Ari stops using it. */
export function ConnectorOffSwitch({
  name,
  enabled,
  onChange,
  testId,
}: {
  name: string;
  enabled: boolean;
  /** A returned promise locks the switch until it settles. */
  onChange: (enabled: boolean) => void | Promise<unknown>;
  testId?: string;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  const flip = (checked: boolean) => {
    const result = onChange(checked);
    if (!(result instanceof Promise)) return;
    setSaving(true);
    void result.finally(() => setSaving(false)).catch(() => undefined);
  };

  return (
    <Switch
      size="small"
      checked={enabled}
      disabled={saving}
      onClick={(e) => e.stopPropagation()}
      onChange={(_, checked) => flip(checked)}
      slotProps={{
        input: {
          role: 'switch',
          'aria-label': t(enabled ? 'settings.connectors.turnOff' : 'settings.connectors.turnOn', { name }),
        },
      }}
      data-testid={testId}
      sx={{ flexShrink: 0 }}
    />
  );
}

const STATE_LABEL_KEY = {
  connected: 'settings.connectors.connected',
  off: 'settings.connectors.off',
} as const;

/** The one chip every connector row ends with. Off and Connected share one box. */
export function ConnectorStateChip({ state }: { state: 'connected' | 'off' }) {
  const { t } = useTranslation();
  const other = state === 'off' ? 'connected' : 'off';
  return (
    <StatusChip
      color={state === 'off' ? 'default' : 'success'}
      label={t(STATE_LABEL_KEY[state])}
      sx={{
        flexShrink: 0,
        height: 22,
        '& .MuiChip-label': {
          px: 1.25,
          textAlign: 'center',
          // A hidden copy of the other label sizes both chips to the wider word.
          '&::after': {
            content: JSON.stringify(t(STATE_LABEL_KEY[other])),
            display: 'block',
            height: 0,
            visibility: 'hidden',
          },
        },
      }}
    />
  );
}

export const connectorOffRowSx = (theme: Theme) => ({
  ...connectorRowSx(theme),
  borderStyle: 'dashed',
  '& .MuiAvatar-root': { opacity: 0.5 },
});

export const connectorActionButtonSx = {
  minWidth: 96,
  height: 24,
  minHeight: 24,
  fontSize: 12,
  px: 1,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  borderRadius: 1,
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
} as const;
